"use client";

import { Children, useEffect, useRef, useState, type ReactNode } from "react";

// "idle" keeps cards visible for no-JS and reduced-motion users; the reveal
// only kicks in once hydration confirms animation is wanted.
type Phase = "idle" | "hidden" | "shown";

interface RevealGridProps {
  className: string;
  children: ReactNode;
}

/**
 * Grid wrapper that staggers a fade-up reveal of its children when scrolled
 * into view. Children stay server-rendered; each is wrapped in a grid cell
 * that carries the animation.
 */
export function RevealGrid({ className, children }: RevealGridProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>("idle");

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const el = ref.current;
    if (!el) return;
    setPhase("hidden");
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPhase("shown");
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={className}>
      {Children.map(children, (child, i) => (
        <div
          className={`[&>*]:h-full ${phase === "hidden" ? "opacity-0" : ""}`}
          style={
            phase === "shown"
              ? { animation: `cb-fade-up 0.5s ease-out ${i * 0.12}s both` }
              : undefined
          }
        >
          {child}
        </div>
      ))}
    </div>
  );
}
